<script setup lang="ts">
import type { IconType } from './IconType'
import type { Size } from './TextCommon'

import BaseIcon from '~/components/foundations/BaseIcon.vue'

const {
  size = 'medium',
  border = false,
  disabled = false,
  selected = false,
  color = 'neutral',
  hoverEffect = true,
} = defineProps<{
  size?: Size
  border?: boolean
  disabled?: boolean
  selected?: boolean
  iconType: IconType
  color?: 'neutral' | 'brand'
  hoverEffect?: boolean
}>()

const SIZE_ATTRIBUTES = {
  large: {
    buttonSize: '48px',
    iconSize: 24,
  },
  medium: {
    buttonSize: '40px',
    iconSize: 18,
  },
  small: {
    buttonSize: '32px',
    iconSize: 18,
  },
}

const iconSize = computed(() => {
  return SIZE_ATTRIBUTES[size].iconSize
})

const buttonSize = computed(() => {
  return SIZE_ATTRIBUTES[size].buttonSize
})

const iconColor = computed(() => {
  return color === 'brand' ? 'brand-strong' : 'neutral-default'
})
</script>

<template>
  <button
    class="base-icon-button"
    :aria-label="iconType"
    :class="{ border, disabled, selected, brand: color === 'brand', 'no-hover': !hoverEffect }"
    :style="{ width: buttonSize, height: buttonSize }"
    :disabled="disabled"
  >
    <BaseIcon :type="iconType" :size="iconSize" :color="iconColor" />
  </button>
</template>

<style lang="scss" scoped>
.base-icon-button {
  display: flex;
  justify-content: center;
  align-items: center;
  background: none;
  border: none;
  border-radius: 4px;
  cursor: pointer;

  &:not(:disabled):hover,
  &:not(:disabled).selected {
    background-color: var(--semantic-tokens-neutral-xweak-surface);
  }

  &.no-hover {
    &:not(:disabled):hover {
      background-color: transparent;
    }
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.3;
  }
}

.border {
  border: 1px solid var(--semantic-tokens-neutral-weak-border);
}

.brand {
  &:not(:disabled):hover,
  &:not(:disabled).selected {
    background-color: var(--semantic-tokens-brand-weak-surface);
  }

  &.border {
    border-color: var(--semantic-tokens-brand-strong-border);
  }
}
</style>
